import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { Store } from '@ngxs/store';
import { SetCookielessTrackingRequired } from '@platform-frontend/account';
import { WindowParentService } from '@platform-frontend/util';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { HostService } from '../integration/iframe/host.service';
import { CheckoutVendorType } from './model/checkout-vendor-type';
import { WebCheckoutIntegrationMode } from './model/web-checkout-integration-mode';
import { CheckoutStandaloneService } from './standalone/checkout-standalone.service';

export interface IIntegrationService {
  init(): void;
  isApplicationVisible(): Observable<boolean>;
  isRestartingOrShuttingDown(): Observable<boolean>;
  getVendorType(): CheckoutVendorType;
  restart(restartUrl?: string): Observable<unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class WebCheckoutIntegrationService {
  constructor(
    private checkoutStandaloneService: CheckoutStandaloneService,
    private hostService: HostService,
    private store: Store,
    private windowParentService: WindowParentService
  ) {}

  private integrationMode: WebCheckoutIntegrationMode;

  init(): void {
    if (this.windowParentService.insideIframe()) {
      this.integrationMode = 'iframe';
      this.hostService.init();
    } else {
      this.integrationMode = 'standalone';
      this.checkoutStandaloneService.init();
    }
    this.setCookielessTrackingRequired();
  }

  getIntegrationMode(): WebCheckoutIntegrationMode {
    return this.integrationMode;
  }

  isApplicationVisible(): Observable<boolean> {
    return this.integrationMode === 'iframe' ? this.hostService.isApplicationVisible() : this.checkoutStandaloneService.isApplicationVisible();
  }

  isRestartingOrShuttingDown(): Observable<boolean> {
    return this.integrationMode === 'iframe' ? this.hostService.isRestartingOrShuttingDown() : this.checkoutStandaloneService.isRestartingOrShuttingDown();
  }

  getVendorType(): CheckoutVendorType {
    return this.integrationMode === 'iframe' ? this.hostService.getVendorType() : this.checkoutStandaloneService.getVendorType();
  }

  restart(restartUrl?: string): Observable<unknown> {
    return this.integrationMode === 'iframe' ? this.hostService.restart(restartUrl) : this.checkoutStandaloneService.restart(restartUrl);
  }

  private setCookielessTrackingRequired(): void {
    combineLatest([this.isApplicationVisible(), this.isRestartingOrShuttingDown()])
      .pipe(
        map(([isApplicationVisible, isRestartingOrShuttingDown]) => isApplicationVisible && !isRestartingOrShuttingDown),
        debounceTime(0),
        distinctUntilChanged(),
        switchMap(isRequired => this.store.dispatch(new SetCookielessTrackingRequired(isRequired)))
      )
      .subscribe();
  }
}

export const WebCheckoutIntegrationServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (webCheckoutService: WebCheckoutIntegrationService) => () => webCheckoutService.init(),
  deps: [WebCheckoutIntegrationService],
  multi: true
};
